import { api } from "@/lib/api";
import { tarihSaat } from "@/lib/veri";
import { Band, Buton, Etiket, Kutu, Kv, tabloBas, tabloHucre } from "@/components/parcalar";
import { belgeLinki } from "@/components/pdf-sayfa";
import { cevir } from "@/lib/dil";
import { cn } from "@/lib/cn";
import { donusturucuRozeti, gecenMetni, saat, sureMetni } from "@/lib/mrb-toplu";
import { bekleyenDurumu, raporAdi, type RaporTuru } from "@/lib/rapor-toplu";
import { KuyrukEtiketi, hataAyrintisi, type IsGorunumu } from "./SokumKuyruk";

/**
 * D-065: toplu yüklenen Disposition ve Rework raporlarının dönüşüm durumu ekranda (Inspection verification). Veri `GET /api/rapor`:
 * "Report converter" kutusu ve "Reports waiting for their input" listesi aynı anın verisidir.
 */

/**
 * "Report converter" kutusunun verisi; dönüştürücü tektir, o an hangi türle meşgulse `donusturulen` onu söyler (`ad`: riser, MRB'de SN).
 * D-068: Assembly raporuyla meşgulse tür "assembly"dir.
 */
export interface RaporDonusturucu {
  sonBaglanti: string | null;
  anahtarAd: string | null;
  anahtarOlusturan: string | null;
  aktifAnahtarVar: boolean;
  donusturulen: { tur: "sokum" | "mrb" | "assembly" | "hidrotest" | "ndt" | "weldmap" | "pwht" | "ndt_personel" | "kaynakci_wpq" | "alet_kalibrasyon" | "sarf_belgesi" | "wps_belgesi" | "teslim_listesi" | "malzeme_sertifikasi" | "uygunluk_sertifikasi" | "satin_alma" | RaporTuru; ad: string; tutmaBaslangic: string | null; tutmaBitis: string | null } | null;
  disposition: { sirada: number; hatali: number };
  rework: { sirada: number; hatali: number };
}

/** Girdisini bekleyen rapor. `hazir` false: rework işi sırada ama riserin disposition girdisi yok, dönüştürücüye verilmez. */
export interface BekleyenRapor {
  id: string;
  tur: RaporTuru;
  rn: string;
  riserId: string;
  pdfDokumanId: string;
  /** Belge kaldırıldıysa null. */
  pdfAd: string | null;
  durum: "girdi_bekliyor";
  hazir: boolean;
  yukleyen: string;
  ts: string;
  is: IsGorunumu;
  kuyrugaEklenebilir: boolean;
}

export interface RaporListesi {
  donusturucu: RaporDonusturucu;
  bekleyenler: BekleyenRapor[];
}

/** Kuyruk işlemi: sıraya ekle ("Add to queue", hatadan "Retry") ya da sıradan çıkar ("Remove from queue"). */
export const raporSiraIslemi = (id: string, islem: "ekle" | "cikar") => api.post(`/rapor/${id}/sira`, { islem });

/** Kuyruk düğmelerinin yetkisi raporu yükleyebilen kişinindir (sunucudaki `RAPOR_YETKISI`). */
const YETKI = { disposition: "evrak", rework: "dogrula" } as const;

/**
 * D-065 "Report converter" (söküm listesindeki "Disassembly report converter" kutusunun eşi): son bağlantı (sunucu saatiyle),
 * anahtar, şu an dönüştürülen belge ve tutma süresi, türe göre sırada ve hatalı rapor sayısı. Dönüştürücü tektir: bir söküm raporu
 * ya da MRB ile meşgulse o görünür.
 */
export function RaporDonusturucuKutusu({ d, simdi }: { d: RaporDonusturucu; simdi: number }) {
  const sirada = d.disposition.sirada + d.rework.sirada;
  const r = donusturucuRozeti({ sonBaglanti: d.sonBaglanti, sirada }, simdi);
  const rozet =
    r.tur === "bagli" ? <Etiket tone="ok">{cevir("connected")}</Etiket>
    : r.tur === "kopuk" ? <Etiket tone={r.uyari ? "warn" : "neutral"}>{cevir("not connected for {0}", sureMetni(r.gecen ?? 0))}</Etiket>
    : <Etiket>{cevir("not connected yet")}</Etiket>;
  const t = d.donusturulen;
  const ad = !t ? "" : t.tur === "sokum" ? cevir("Disassembly report {0}", t.ad) : t.tur === "mrb" ? `MRB ${t.ad}` : t.tur === "assembly" ? cevir("Assembly report {0}", t.ad) : t.tur === "hidrotest" ? cevir("Hydrotest certificate {0}", t.ad) : t.tur === "ndt" ? cevir("NDT report {0}", t.ad) : t.tur === "weldmap" ? cevir("Weld map {0}", t.ad) : t.tur === "pwht" ? cevir("PWHT chart {0}", t.ad) : t.tur === "ndt_personel" ? cevir("NDT certificate {0}", t.ad) : t.tur === "kaynakci_wpq" ? cevir("Welder document {0}", t.ad) : t.tur === "alet_kalibrasyon" ? cevir("Calibration certificate {0}", t.ad) : t.tur === "sarf_belgesi" ? cevir("Consumable document {0}", t.ad) : t.tur === "wps_belgesi" ? cevir("WPS document {0}", t.ad) : t.tur === "teslim_listesi" ? cevir("Packing list {0}", t.ad) : t.tur === "malzeme_sertifikasi" ? cevir("Material certificate {0}", t.ad) : t.tur === "uygunluk_sertifikasi" ? cevir("Certificate of conformity {0}", t.ad) : t.tur === "satin_alma" ? cevir("Purchase order {0}", t.ad) : raporAdi(t.tur, t.ad);
  const tutma = t ? [t.tutmaBaslangic ? cevir("since {0}", saat(t.tutmaBaslangic)) : null, t.tutmaBitis ? cevir("held until {0}", saat(t.tutmaBitis)) : null].filter(Boolean).join(" · ") : "";
  const bekleyen = (ad: string, k: { sirada: number; hatali: number }) => <div>{ad} <b className="tnum">{k.sirada}</b> {cevir("queued")} <span className="text-ink-3">·</span> <b className="tnum">{k.hatali}</b> {cevir("failed")}</div>;
  return (
    <Kutu baslik={cevir("Report converter")} aksiyon={rozet}>
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kv ad={cevir("Last connected")}>{d.sonBaglanti ? <><b>{tarihSaat(d.sonBaglanti)}</b> <span className="text-ink-3">· {gecenMetni(r.gecen ?? 0)}</span></> : cevir("never")}</Kv>
        <Kv ad={cevir("Key")}>{d.anahtarAd ? <>{d.anahtarAd} <span className="text-ink-3">· {cevir("created by System Administrator")}</span></> : "—"}</Kv>
        <Kv ad={cevir("Now converting")}>
          {t ? <><b className="font-mono">{ad}</b>{tutma && <> <span className="text-ink-3">· {tutma}</span></>}</> : "—"}
        </Kv>
        <Kv ad={cevir("Waiting")}>{bekleyen("Disposition", d.disposition)}{bekleyen("Rework", d.rework)}</Kv>
      </div>
      {!d.aktifAnahtarVar && <Band tip="not" className="mt-3">{cevir("No active converter key. A System Administrator creates one in Admin.")}</Band>}
      {r.uyari && <Band tip="not" className="mt-3">{cevir("The converter has not connected for {0} and {1} reports are waiting. The converter computer may be off or asleep.", sureMetni(r.gecen ?? 0), sirada)}</Band>}
      <Band tip="bilgi" className="mt-3">{cevir("The converter runs on a computer outside OASIS. While that computer is on, it checks the queue every 15 minutes and converts one document at a time, in this order: Disassembly reports, MRBs, Disposition reports, Rework reports, Assembly reports. It reads the report PDF with the engine of that report and delivers the input excel. The excel is read right away, with the same checks as a manual upload and some stricter ones, and the items appear on this page. An excel that fails the checks is not read and the report shows as failed, with the reason. A Rework report is converted only after the disposition input of its riser is in the system. An input uploaded by hand is never replaced by the converter. If the computer sleeps during a conversion, the report goes back to the queue when its hold time runs out.")}</Band>
    </Kutu>
  );
}

/**
 * Bekleyen raporun "State" hücresi: beklenen girdi + işin durumu ve düğmesi. queued · {n} (+ Remove from queue) · waiting for the
 * disposition input (rework işi sırada, ön koşulunu bekliyor) · converting (başlama saati, düğme yok) · failed (+ Retry, altında
 * zaman ve sebep) · işi yok (+ Add to queue, sunuluyorsa). "Proceed without excel" yoktur: riser girdiyi beklerken tutulmaz.
 */
function BekleyenDurumu({ b, islem, mesgul }: { b: BekleyenRapor; islem: (islem: "ekle" | "cikar") => void; mesgul: boolean }) {
  const { durum, dugme } = bekleyenDurumu(b);
  const beklenen = <Etiket tone="warn">{b.tur === "disposition" ? cevir("disposition input expected") : cevir("rework input expected")}</Etiket>;
  const dugmeAdi = dugme === "cikar" ? cevir("Remove from queue") : dugme === "tekrar" ? cevir("Retry") : cevir("Add to queue");
  const ayrinti = durum === "hata" ? hataAyrintisi(b.is) : "";
  return (
    <>
      {beklenen}
      {durum === "on_kosul" ? <> <Etiket>{cevir("waiting for the disposition input")}</Etiket></> : durum !== "yok" && <> <KuyrukEtiketi k={b.is} /></>}
      {durum === "donusturuluyor" && b.is.tutmaBaslangic && <> <span className="text-xs text-ink-3">{cevir("since {0}", saat(b.is.tutmaBaslangic))}</span></>}
      {dugme && <> <Buton kucuk islem={YETKI[b.tur]} mesgul={mesgul} onClick={() => islem(dugme === "cikar" ? "cikar" : "ekle")}>{dugmeAdi}</Buton></>}
      {ayrinti && <div className="mt-1 max-w-lg text-xs text-bad">{ayrinti}</div>}
    </>
  );
}

/**
 * D-065 "Reports waiting for their input": girdisi bekleyen Disposition ve Rework raporları, rapor başına bir satır. Liste boşsa
 * kutu çizilmez. `mesgul`: kuyruk işlemi süren kaydın kimliği.
 */
export function BekleyenRaporlar({ liste, islem, mesgul }: { liste: BekleyenRapor[]; islem: (id: string, islem: "ekle" | "cikar") => void; mesgul: string | null }) {
  if (!liste.length) return null;
  return (
    <Kutu baslik={cevir("Reports waiting for their input")} padded={false}>
      <div className="scroll-thin overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead><tr className="border-b border-line"><th className={tabloBas}>RN</th><th className={tabloBas}>{cevir("Report")}</th><th className={tabloBas}>PDF</th><th className={tabloBas}>{cevir("State")}</th></tr></thead>
          <tbody>{liste.map((b) => (
            <tr key={b.id} className="border-b border-line last:border-0">
              <td className={tabloHucre}><b className="font-mono">{b.rn}</b></td>
              <td className={tabloHucre}>{b.tur === "disposition" ? "Disposition" : "Rework"}</td>
              <td className={cn(tabloHucre, "text-xs")}>{b.pdfAd ? <a href={belgeLinki(b.pdfDokumanId)} target="_blank" rel="noreferrer" className="underline">{b.pdfAd}</a> : "—"}</td>
              <td className={tabloHucre}><BekleyenDurumu b={b} mesgul={mesgul === b.id} islem={(i) => islem(b.id, i)} /></td>
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Kutu>
  );
}
